import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import CardActionArea from '@mui/material/CardActionArea';
import Stack from '@mui/material/Stack';
import { imageShape } from '../shapes/ImageShapes';
import NumberField from './UI/NumberField';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import DragIndicator from '@mui/icons-material/DragIndicator';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import TextOverlay from './UI/TextOverlay';
import { appendImages } from '../image_utilities/AppendImages';


const FullWidthBox = styled(Box)({
  width: '100%',
});

const AppendCard = styled(Card)({
  marginTop: '5px',
});

const PaddedCardActionArea = styled(CardActionArea)({
  padding: '10px',
});

const Content = styled(Box)({
  padding: '10px',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
});

class Append extends React.Component {
  state = {
    orderedImageNames: [],
    currentStepName: '',
    delays: {},
    error: null,
    loops: {},
    inputIsValid: {
      loops: true,
      delays: true,
    },
    hoveredStep: '',
  };

  componentDidUpdate() {
    // If we added or removed an image, reset ordering
    if (this.props.images.length !== this.state.orderedImageNames.length) {
      this.setState({ orderedImageNames: this.props.images.map((image) => image.name) });
    }
  }

  handleSubmit = async () => {
    if (!this.getInputIsValid()) return;
    this.setState({ error: null });
    try {
      const images = await appendImages(this.props.images, this.getOrderedImageNames(),
        this.state.loops, this.state.delays);
      this.props.onImageChangeStart();
      this.props.onImageChange(images);
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  getOrderedImageNames = () => {
    return this.state.orderedImageNames.length > 0
      ? this.state.orderedImageNames
      : this.props.images.map((image) => image.name);
  };

  getInputIsValid = () => {
    return Object.keys(this.state.inputIsValid).reduce(
      (acc, name) => acc && Object.values(this.state.inputIsValid[name]).every(Boolean),
      true
    );
  };

  handleDragEnd = (result) => {
    if (!result.destination) {
      // Dropped somewhere it doesn't belong
      return;
    }

    this.setState({
      orderedImageNames: this.reorder(
        this.getOrderedImageNames(),
        result.source.index,
        result.destination.index
      ),
    });
  };

  reorder = (list, oldIndex, newIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(oldIndex, 1);
    result.splice(newIndex, 0, removed);
    return result;
  };

  getAppendCardBackgroundColor = (snapshot) => (snapshot.isDragging ? 'blanchedAlmond' : '');

  getDragHandleBackgroundColor = (index) =>
    this.state.hoveredStep === index ? 'blanchedAlmond' : '';

  getDroppableListBackgroundColor = (snapshot) => {
    if (snapshot.isDraggingOver) {
      return 'lightgreen';
    }
    // Dragging is happening, but not over the droppable
    if (snapshot.draggingFromThisWith) {
      return 'lightpink';
    }

    return '';
  };

  getTextOverlayColor = (name) =>
    this.state.currentStepName === name ? 'primary' : 'textSecondary';

  showCardContent = (name, snapshot) =>
    this.state.currentStepName === name && !snapshot.isUsingPlaceholder;

  getIndexOfCurrentStep = () => this.getOrderedImageNames().indexOf(this.state.currentStepName);

  render() {
    return this.props.images.length > 1 ? (
      <>
        <Divider variant="middle" />
        <Typography variant="body2" sx={{ my: 1 }}>
          Plays clips in order at their original speed. Blank Loops uses a finite source's total plays,
          or one play for a source that repeats forever.
        </Typography>
        {this.state.error && <Typography role="alert" color="error">{this.state.error}</Typography>}
        <Stack useFlexGap sx={{ width: '100%' }} spacing={1} justifyContent="flex-end" alignItems="flex-start">
          <FullWidthBox>
            <DragDropContext onDragEnd={this.handleDragEnd}>
              <Droppable droppableId="layers" direction="vertical">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{ backgroundColor: this.getDroppableListBackgroundColor(snapshot) }}
                  >
                    {this.getOrderedImageNames().map((name, index) => (
                      <React.Fragment key={name}>
                        <Draggable draggableId={name} index={index} x>
                          {(provided, snapshot) => (
                            <div ref={provided.innerRef} {...provided.draggableProps}>
                              <AppendCard
                                className="glint-hover-lift"
                                variant="outlined"
                                square
                                style={{
                                  backgroundColor: this.getAppendCardBackgroundColor(snapshot),
                                }}
                              >
                                <Box display="flex" alignItems="center">
                                  <Box
                                    {...provided.dragHandleProps}
                                    display="flex"
                                    alignItems="center"
                                    onMouseEnter={() => this.setState({ hoveredStep: index })}
                                    onMouseMove={() => this.setState({ hoveredStep: index })}
                                    onMouseLeave={() => this.setState({ hoveredStep: '' })}
                                    style={{
                                      backgroundColor: this.getDragHandleBackgroundColor(index),
                                    }}
                                  >
                                    <DragIndicator
                                      color={
                                        this.state.hoveredStep === index ? 'primary' : 'action'
                                      }
                                    />
                                    <TextOverlay
                                      text={index + 1}
                                      textProps={{
                                        color: this.getTextOverlayColor(name),
                                        variant: 'button',
                                      }}
                                      containerProps={{
                                        style: { padding: '5px', paddingLeft: '0px' },
                                      }}
                                    >
                                      <RadioButtonUnchecked
                                        color={
                                          this.state.currentStepName === name ? 'primary' : 'action'
                                        }
                                      />
                                    </TextOverlay>
                                  </Box>
                                  <Divider orientation="vertical" flexItem />
                                  <PaddedCardActionArea
                                    onClick={() =>
                                      this.setState((prevState) => ({
                                        currentStepName:
                                          prevState.currentStepName === name ? '' : name,
                                      }))
                                    }
                                  >
                                    <Box display="flex" justifyContent="space-between">
                                      {/* Hidden left icon to balance the space between flex*/}
                                      <ExpandLess style={{ visibility: 'hidden' }} />
                                      <Typography>{name}</Typography>
                                      {this.state.currentStepName === name ? (
                                        <ExpandLess />
                                      ) : (
                                        <ExpandMore />
                                      )}
                                    </Box>
                                  </PaddedCardActionArea>
                                </Box>
                                {this.showCardContent(name, snapshot) && (
                                  <>
                                    <Divider />
                                    <Content>
                                      <Typography variant="h6">Image Details</Typography>
                                      <NumberField
                                        label="Loops"
                                        allowDecimals
                                        additionalValidation={value => value === '' || Number.isSafeInteger(value)}
                                        value={
                                          this.state.loops[name] != null
                                            ? this.state.loops[name]
                                            : ''
                                        }
                                        onChange={(value, isValid) => {
                                          this.setState((prevState) => ({
                                            loops: { ...prevState.loops, [name]: value },
                                            inputIsValid: {
                                              ...prevState.inputIsValid,
                                              [name]: {
                                                ...prevState.inputIsValid[name],
                                                loops: isValid,
                                              },
                                            },
                                          }));
                                        }}
                                        allowPositive
                                        allowEmpty
                                      />
                                      <NumberField
                                        label="Wait Between Frames"
                                        value={
                                          this.state.delays[name] != null
                                            ? this.state.delays[name]
                                            : ''
                                        }
                                        onChange={(value, isValid) => {
                                          this.setState((prevState) => ({
                                            delays: { ...prevState.delays, [name]: value },
                                            inputIsValid: {
                                              ...prevState.inputIsValid,
                                              [name]: {
                                                ...prevState.inputIsValid[name],
                                                delays: isValid,
                                              },
                                            },
                                          }));
                                        }}
                                        suffix="ms"
                                        allowPositive
                                        allowEmpty
                                      />
                                    </Content>
                                  </>
                                )}
                              </AppendCard>
                            </div>
                          )}
                        </Draggable>
                      </React.Fragment>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </FullWidthBox>
          <FullWidthBox>
            <Box display="flex" justifyContent="space-between">
              {this.getIndexOfCurrentStep() > 0 && (
                <Button
                  className="glint-tactile"
                  variant="contained"
                  color="secondary"
                  disabled={!this.getInputIsValid()}
                  onClick={() =>
                    this.setState((prevState) => ({
                      currentStepName: this.state.orderedImageNames[
                        this.getIndexOfCurrentStep() - 1
                      ],
                    }))
                  }
                >
                  Back
                </Button>
              )}
              <Button
                className="glint-tactile"
                variant="contained"
                color="primary"
                disabled={!this.getInputIsValid()}
                onClick={
                  this.getIndexOfCurrentStep() < this.getOrderedImageNames().length - 1 &&
                  this.state.currentStepName !== ''
                    ? () =>
                        this.setState((prevState) => ({
                          currentStepName:
                            prevState.orderedImageNames[this.getIndexOfCurrentStep() + 1],
                        }))
                    : this.handleSubmit
                }
              >
                {this.getIndexOfCurrentStep() < this.getOrderedImageNames().length - 1 &&
                this.state.currentStepName !== ''
                  ? 'Next'
                  : 'Submit'}
              </Button>
            </Box>
          </FullWidthBox>
        </Stack>
      </>
    ) : (
      <Typography>
        You will be able append images together here so they play one after the other, but first you
        have to add at least two!
      </Typography>
    );
  }
}

Append.propTypes = {
  images: PropTypes.arrayOf(imageShape),
  onImageChange: PropTypes.func.isRequired,
  onImageChangeStart: PropTypes.func,
};
Append.defaultProps = {
  images: [],
  onImageChange() {},
  onImageChangeStart() {},
};

export default Append;
